import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { Reveal } from "@/components/Reveal";
import { formatMessage } from "@/lib/formatMessage";
import { NEWSLETTER_TEMPLATES } from "@/lib/newsletterTemplates";
import { QrCode, ArrowUpRight, Users, Heart, CalendarCheck, MessageSquare, TrendingUp, Search, Download, Pencil, Trash2, X, Mail, UserPlus, Clock, Bold, Heading, Link2, Eye, Activity, GripVertical, Volume2, Loader2, Radio, Upload, Music } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, Cell } from "recharts";
import { toast } from "sonner";

const fmtDur = (s) => { const m = Math.floor((s || 0) / 60); const sec = Math.round((s || 0) % 60); return `${m}:${String(sec).padStart(2, "0")}`; };
const fmtHour = (h) => { if (h === 24) return "Midnight"; const x = h % 24; const ap = x < 12 ? "AM" : "PM"; const d = x % 12 === 0 ? 12 : x % 12; return `${d} ${ap}`; };
const RADIO_CATS = ["worship", "sermon", "teaching", "prayer", "music", "general"];
const TZ_OPTIONS = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "UTC"];

export default function Admin() {
  const { user: me } = useAuth();
  const [settings, setSettings] = useState({ notify_email: "", marquee_words: [] });
  const [marqueeText, setMarqueeText] = useState("");
  const [savingMarquee, setSavingMarquee] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sendingDaily, setSendingDaily] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [trends, setTrends] = useState([]);
  const [activity, setActivity] = useState([]);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [verifiedFilter, setVerifiedFilter] = useState("all");
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [emailUser, setEmailUser] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: "", message: "" });
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", password: "", role: "member", send_welcome: true });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ id: null, audience: "all", subject: "", message: "", scheduled_at: "", recurrence: "none" });
  const [announcements, setAnnouncements] = useState([]);
  const [audienceCount, setAudienceCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(false);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [tplName, setTplName] = useState("");
  const [tplSaving, setTplSaving] = useState(false);
  const [emailTypes, setEmailTypes] = useState([]);
  const [previewKind, setPreviewKind] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [wod, setWod] = useState({ enabled: false, mode: "manual", auto_source: "verses", voice: "onyx", heading: "Word of the Day", scripture: "", message: "" });
  const [savingWod, setSavingWod] = useState(false);
  const [previewingVoice, setPreviewingVoice] = useState(false);
  const voicePreviewRef = useRef(null);
  const [verses, setVerses] = useState([]);
  const [newVerse, setNewVerse] = useState({ scripture: "", message: "" });
  const [addingVerse, setAddingVerse] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [radio, setRadio] = useState({ enabled: true, station_name: "IHIMM Radio", mode: "playlist", external_url: "", external_label: "", tracks: [] });
  const [savingRadio, setSavingRadio] = useState(false);
  const [uploadingTrack, setUploadingTrack] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("general");
  const [trackDragIdx, setTrackDragIdx] = useState(null);
  const trackFileRef = useRef(null);
  const msgRef = useRef(null);

  useEffect(() => {
    api.get("/settings").then((r) => { setSettings(r.data); setMarqueeText((r.data.marquee_words || []).join(", ")); if (r.data.word_of_day) setWod({ enabled: false, mode: "manual", auto_source: "verses", voice: "onyx", heading: "Word of the Day", scripture: "", message: "", ...r.data.word_of_day }); }).catch(() => {});
    api.get("/admin/email-previews").then((r) => { setEmailTypes(r.data); if (r.data?.[0]?.kind) setPreviewKind(r.data[0].kind); }).catch(() => {});
    api.get("/admin/wod-verses").then((r) => setVerses(r.data)).catch(() => {});
    api.get("/contact").then((r) => setInquiries(r.data)).catch(() => {});
    api.get("/admin/analytics").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/giving-trends").then((r) => setTrends(r.data)).catch(() => {});
    api.get("/admin/activity").then((r) => setActivity(r.data)).catch(() => {});
    api.get("/admin/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
    api.get("/admin/templates").then((r) => setCustomTemplates(r.data)).catch(() => {});
    api.get("/admin/radio").then((r) => setRadio(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!bulkOpen) return;
    setAudienceCount(null);
    api.get(`/admin/audience-count?audience=${bulkForm.audience}`).then((r) => setAudienceCount(r.data.count)).catch(() => {});
  }, [bulkOpen, bulkForm.audience]);

  useEffect(() => {
    if (!previewKind) return;
    setPreviewLoading(true);
    api.get(`/admin/email-preview/${previewKind}`)
      .then((r) => { setPreviewHtml(r.data.html); setPreviewSubject(r.data.subject); })
      .catch(() => { setPreviewHtml(""); setPreviewSubject(""); })
      .finally(() => setPreviewLoading(false));
  }, [previewKind]);

  const refreshActivity = () => api.get("/admin/activity").then((r) => setActivity(r.data)).catch(() => {});
  const refreshAnnouncements = () => api.get("/admin/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});

  const changeRole = async (id, role) => {
    try {
      await api.put(`/admin/users/${id}/role`, { role });
      setUsers((us) => us.map((u) => (u.id === id ? { ...u, role, is_supporter: role === "supporter" || u.is_supporter } : u)));
      toast.success("Role updated");
      refreshActivity();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update role"); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/users/${editUser.id}`, {
        name: editUser.name, email: editUser.email, role: editUser.role, is_supporter: editUser.is_supporter,
      });
      setUsers((us) => us.map((u) => (u.id === data.id ? { ...u, ...data } : u)));
      toast.success("User updated");
      setEditUser(null);
      refreshActivity();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update user"); }
    finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${deleteUser.id}`);
      setUsers((us) => us.filter((u) => u.id !== deleteUser.id));
      toast.success("User removed");
      setDeleteUser(null);
      refreshActivity();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove user"); }
    finally { setBusy(false); }
  };

  const sendMemberEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/users/${emailUser.id}/email`, emailForm);
      toast.success(`Email sent to ${emailUser.name}`);
      setEmailUser(null); setEmailForm({ subject: "", message: "" });
      refreshActivity();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send email"); }
    finally { setBusy(false); }
  };

  const addMember = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", addForm);
      setUsers((us) => [data, ...us]);
      toast.success(data.welcome_sent ? "Member created & welcome email sent" : "Member created");
      setAddOpen(false); setAddForm({ name: "", email: "", password: "", role: "member", send_welcome: true });
      refreshActivity();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create member"); }
    finally { setBusy(false); }
  };

  const resetBulk = () => setBulkForm({ id: null, audience: "all", subject: "", message: "", scheduled_at: "", recurrence: "none" });

  const refreshTemplates = () => api.get("/admin/templates").then((r) => setCustomTemplates(r.data)).catch(() => {});

  const saveTemplate = async () => {
    if (!tplName.trim()) { toast.error("Name your template first"); return; }
    if (!bulkForm.subject.trim() || !bulkForm.message.trim()) { toast.error("Add a subject and message first"); return; }
    setTplSaving(true);
    try {
      await api.post("/admin/templates", { name: tplName.trim(), subject: bulkForm.subject, message: bulkForm.message });
      toast.success("Template saved");
      setTplName("");
      refreshTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save template"); }
    finally { setTplSaving(false); }
  };

  const deleteTemplate = async (id) => {
    try { await api.delete(`/admin/templates/${id}`); setCustomTemplates((t) => t.filter((x) => x.id !== id)); toast.success("Template removed"); }
    catch { toast.error("Could not remove template"); }
  };

  const insertFormat = (kind) => {
    const el = msgRef.current;
    const val = bulkForm.message;
    const start = el ? el.selectionStart : val.length;
    const end = el ? el.selectionEnd : val.length;
    const sel = val.slice(start, end);
    let snippet;
    if (kind === "bold") snippet = `**${sel || "bold text"}**`;
    else if (kind === "heading") snippet = `${start > 0 && val[start - 1] !== "\n" ? "\n" : ""}## ${sel || "Heading"}`;
    else snippet = `[${sel || "link text"}](https://)`;
    const next = val.slice(0, start) + snippet + val.slice(end);
    setBulkForm((f) => ({ ...f, message: next }));
    setTimeout(() => { if (el) { el.focus(); el.selectionStart = el.selectionEnd = start + snippet.length; } }, 0);
  };


  const sendBulk = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let data;
      if (bulkForm.id) {
        ({ data } = await api.post(`/admin/announcements/${bulkForm.id}/send`));
        refreshAnnouncements();
      } else {
        ({ data } = await api.post("/admin/bulk-email", bulkForm));
      }
      toast.success(`Sent to ${data.sent} ${data.audience}${data.failed ? ` (${data.failed} failed)` : ""}`);
      setBulkOpen(false); resetBulk();
      refreshActivity();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send announcement"); }
    finally { setBusy(false); }
  };

  const saveDraft = async () => {
    if (!bulkForm.subject.trim() || !bulkForm.message.trim()) { toast.error("Subject and message are required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/announcements", { ...bulkForm, scheduled_at: "" });
      toast.success("Draft saved");
      setBulkOpen(false); resetBulk();
      refreshAnnouncements();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save draft"); }
    finally { setBusy(false); }
  };

  const scheduleSend = async () => {
    if (!bulkForm.subject.trim() || !bulkForm.message.trim()) { toast.error("Subject and message are required"); return; }
    if (!bulkForm.scheduled_at) { toast.error("Pick a date and time to schedule"); return; }
    setBusy(true);
    try {
      await api.post("/admin/announcements", bulkForm);
      toast.success("Announcement scheduled");
      setBulkOpen(false); resetBulk();
      refreshAnnouncements();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not schedule"); }
    finally { setBusy(false); }
  };

  const loadAnnouncement = (a) => {
    setBulkForm({ id: a.id, audience: a.audience || "all", subject: a.subject, message: a.message, scheduled_at: a.scheduled_at ? a.scheduled_at.slice(0, 16) : "", recurrence: a.recurrence || "none" });
    setBulkOpen(true);
  };

  const sendAnnouncementNow = async (a) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/announcements/${a.id}/send`);
      toast.success(`Sent to ${data.sent} ${data.audience}${data.failed ? ` (${data.failed} failed)` : ""}`);
      refreshAnnouncements(); refreshActivity();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };

  const deleteAnnouncement = async (id) => {
    try {
      await api.delete(`/admin/announcements/${id}`);
      setAnnouncements((a) => a.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch { toast.error("Could not remove"); }
  };

  const exportMembers = async () => {
    try {
      const res = await api.get("/admin/members.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "ihimm-members.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not export members"); }
  };

  const filteredUsers = users.filter((u) => {
    const q = query.trim().toLowerCase();
    const matchQ = !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
    const matchR = roleFilter === "all" || u.role === roleFilter;
    const matchV = verifiedFilter === "all"
      || (verifiedFilter === "verified" && u.verified !== false)
      || (verifiedFilter === "unverified" && u.verified === false);
    return matchQ && matchR && matchV;
  });

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.put("/settings", { notify_email: settings.notify_email, reminder_lead_days: Number(settings.reminder_lead_days) || 0 }); toast.success("Settings saved"); }
    catch { toast.error("Could not save"); } finally { setSaving(false); }
  };

  const saveMarquee = async () => {
    setSavingMarquee(true);
    const words = marqueeText.split(",").map((w) => w.trim()).filter(Boolean);
    try {
      const { data } = await api.put("/settings", { notify_email: settings.notify_email, reminder_lead_days: Number(settings.reminder_lead_days) || 0, marquee_words: words });
      setSettings((s) => ({ ...s, marquee_words: data.marquee_words }));
      setMarqueeText((data.marquee_words || []).join(", "));
      toast.success("Marquee updated");
    } catch { toast.error("Could not save marquee"); } finally { setSavingMarquee(false); }
  };

  const saveWod = async () => {
    setSavingWod(true);
    try {
      const { data } = await api.put("/admin/word-of-day", {
        enabled: !!wod.enabled, mode: wod.mode, auto_source: wod.auto_source, voice: wod.voice,
        heading: wod.heading, scripture: wod.scripture, message: wod.message,
      });
      setWod({ enabled: false, mode: "manual", auto_source: "verses", voice: "onyx", heading: "Word of the Day", scripture: "", message: "", ...data });
      toast.success("Word of the Day saved");
    } catch { toast.error("Could not save Word of the Day"); } finally { setSavingWod(false); }
  };

  const previewVoice = async () => {
    if (voicePreviewRef.current) { voicePreviewRef.current.pause(); voicePreviewRef.current = null; }
    setPreviewingVoice(true);
    try {
      const res = await api.get(`/admin/wod-voice-preview?voice=${wod.voice}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      voicePreviewRef.current = audio;
      audio.onended = () => { setPreviewingVoice(false); URL.revokeObjectURL(url); };
      audio.onerror = () => setPreviewingVoice(false);
      await audio.play();
    } catch { setPreviewingVoice(false); toast.error("Could not preview voice"); }
  };

  const addVerse = async () => {
    if (!newVerse.message.trim()) { toast.error("Enter a verse message"); return; }
    setAddingVerse(true);
    try {
      const { data } = await api.post("/admin/wod-verses", newVerse);
      setVerses((v) => [...v, data]);
      setNewVerse({ scripture: "", message: "" });
      toast.success("Verse added");
    } catch { toast.error("Could not add verse"); } finally { setAddingVerse(false); }
  };

  const deleteVerse = async (id) => {
    try {
      await api.delete(`/admin/wod-verses/${id}`);
      setVerses((v) => v.filter((x) => x.id !== id));
    } catch { toast.error("Could not delete verse"); }
  };

  const dropVerse = async (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); return; }
    const next = [...verses];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, moved);
    setVerses(next);
    setDragIdx(null);
    try { await api.put("/admin/wod-verses/reorder", { ids: next.map((v) => v.id) }); }
    catch { toast.error("Could not save order"); }
  };

  const sendDaily = async () => {
    setSendingDaily(true);
    try {
      const { data } = await api.post("/admin/send-daily");
      toast.success(`"${data.title}" sent to ${data.sent} member${data.sent === 1 ? "" : "s"}.`);
    } catch { toast.error("Could not send"); } finally { setSendingDaily(false); }
  };

  const saveRadio = async () => {
    setSavingRadio(true);
    try {
      const { data } = await api.put("/admin/radio/settings", {
        enabled: !!radio.enabled, station_name: radio.station_name || "IHIMM Radio",
        mode: radio.mode, external_url: radio.external_url || "", external_label: radio.external_label || "",
        schedule_enabled: !!radio.schedule_enabled, timezone: radio.timezone || "America/New_York",
        blocks: radio.blocks || [],
      });
      setRadio((r) => ({ ...r, ...data }));
      toast.success("Radio settings saved");
    } catch { toast.error("Could not save radio settings"); } finally { setSavingRadio(false); }
  };

  const uploadTrack = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setUploadingTrack(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", uploadCategory);
      const { data } = await api.post("/admin/radio/tracks", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRadio((r) => ({ ...r, tracks: [...(r.tracks || []), data] }));
      toast.success(`"${data.title}" added to the station`);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not upload track"); }
    finally { setUploadingTrack(false); }
  };

  const deleteTrack = async (id) => {
    try {
      await api.delete(`/admin/radio/tracks/${id}`);
      setRadio((r) => ({ ...r, tracks: (r.tracks || []).filter((t) => t.id !== id) }));
      toast.success("Track removed");
    } catch { toast.error("Could not remove track"); }
  };

  const dropTrack = async (targetIdx) => {
    if (trackDragIdx === null || trackDragIdx === targetIdx) { setTrackDragIdx(null); return; }
    const next = [...(radio.tracks || [])];
    const [moved] = next.splice(trackDragIdx, 1);
    next.splice(targetIdx, 0, moved);
    setRadio((r) => ({ ...r, tracks: next }));
    setTrackDragIdx(null);
    try { await api.put("/admin/radio/tracks/reorder", { ids: next.map((t) => t.id) }); }
    catch { toast.error("Could not save order"); }
  };

  const setTrackCategory = async (id, category) => {
    setRadio((r) => ({ ...r, tracks: (r.tracks || []).map((t) => (t.id === id ? { ...t, category } : t)) }));
    try { await api.put(`/admin/radio/tracks/${id}`, { category }); }
    catch { toast.error("Could not update track"); }
  };

  return (
    <div className="min-h-screen bg-bone pt-32 pb-24">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-rust">Admin Console</span>
          <h1 className="font-serif font-light text-5xl md:text-7xl tracking-tight mt-4">Settings & Inquiries</h1>
        </Reveal>

        {/* ANALYTICS */}
        <Reveal delay={0.05}>
          <div className="mt-16" data-testid="admin-analytics">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp size={20} className="text-olive" strokeWidth={1.5} />
              <h2 className="font-serif text-3xl">Analytics</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-charcoal/10 border border-charcoal/10">
              <Stat icon={Users} label="Total Members" value={stats?.users.total} sub={`+${stats?.users.new_30d ?? 0} in 30 days`} testid="stat-users" />
              <Stat icon={Activity} label="Active (7 days)" value={stats?.users.active_7d} sub={`${stats?.users.active_30d ?? 0} active in 30 days`} testid="stat-active" />
              <Stat icon={Heart} label="Supporters" value={stats?.users.supporters} sub={`${stats?.giving.active_recurring ?? 0} recurring`} testid="stat-supporters" />
              <Stat icon={TrendingUp} label="Donations" value={stats ? `$${Number(stats.giving.donation_total).toLocaleString()}` : undefined} sub={`${stats?.giving.donation_count ?? 0} gifts`} testid="stat-donations" />
              <Stat icon={CalendarCheck} label="Tickets Sold" value={stats?.events.tickets_sold} sub={`$${Number(stats?.events ? stats.giving.ticket_revenue : 0).toLocaleString()} · ${stats?.events.checked_in ?? 0} checked in`} testid="stat-tickets" />
              <Stat icon={CalendarCheck} label="Events" value={stats?.events.total} sub={`${stats?.events.upcoming ?? 0} upcoming`} testid="stat-events" />
              <Stat icon={MessageSquare} label="Feed Posts" value={stats?.community.feed_posts} testid="stat-feed" />
              <Stat icon={Heart} label="Prayers" value={stats?.community.prayers} sub={`${stats?.community.answered_prayers ?? 0} answered`} testid="stat-prayers" />
              <Stat icon={MessageSquare} label="Inquiries" value={stats?.community.inquiries} testid="stat-inquiries" />
            </div>
          </div>
        </Reveal>

        {/* GIVING TRENDS */}
        <Reveal delay={0.06}>
          <div className="mt-12 border border-charcoal/10 p-8" data-testid="giving-trends">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
              <div className="flex items-center gap-3">
                <TrendingUp size={20} className="text-olive" strokeWidth={1.5} />
                <h2 className="font-serif text-3xl">Giving Trends</h2>
              </div>
              <span className="font-mono text-xs uppercase tracking-widest text-charcoal/40">Last 12 months</span>
            </div>
            {trends.some((t) => t.total > 0) ? (
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={220}>
                  <BarChart data={trends} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                    <XAxis dataKey="label" tick={{ fontSize: 11, fontFamily: "monospace", fill: "#101B3D80" }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: "#101B3D0a" }}
                      contentStyle={{ border: "1px solid #101B3D22", borderRadius: 0, fontFamily: "monospace", fontSize: 12 }}
                      formatter={(v, n, p) => [`$${Number(v).toLocaleString()} · ${p.payload.count} gift${p.payload.count === 1 ? "" : "s"}`, "Donations"]} />
                    <Bar dataKey="total" radius={[2, 2, 0, 0]}>
                      {trends.map((t, i) => (
                        <Cell key={i} fill={i === trends.length - 1 ? "#E4A316" : "#1E3A78"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="font-sans text-charcoal/50 py-10 text-center">No donations recorded yet. Trends will appear as gifts come in.</p>
            )}
          </div>
        </Reveal>

        {/* USERS */}
        <Reveal delay={0.08}>
          <div className="mt-16" data-testid="admin-users">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
              <div className="flex items-center gap-3">
                <Users size={20} className="text-olive" strokeWidth={1.5} />
                <h2 className="font-serif text-3xl">Users ({filteredUsers.length})</h2>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
                  <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name or email" data-testid="user-search"
                    className="pl-9 pr-3 py-2.5 border border-charcoal/20 bg-transparent font-sans text-sm w-56 focus:outline-none focus:border-olive" />
                </div>
                <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="user-role-filter"
                  className="border border-charcoal/20 bg-transparent px-3 py-2.5 font-mono text-xs uppercase tracking-widest focus:outline-none focus:border-olive">
                  <option value="all">All Roles</option>
                  <option value="member">Members</option>
                  <option value="supporter">Supporters</option>
                  <option value="admin">Admins</option>
                </select>
                <select value={verifiedFilter} onChange={(e) => setVerifiedFilter(e.target.value)} data-testid="user-verified-filter"
                  className="border border-charcoal/20 bg-transparent px-3 py-2.5 font-mono text-xs uppercase tracking-widest focus:outline-none focus:border-olive">
                  <option value="all">All Status</option>
                  <option value="verified">Verified</option>
                  <option value="unverified">Awaiting Verification</option>
                </select>
                <button onClick={exportMembers} data-testid="export-members-btn"
                  className="inline-flex items-center gap-2 border border-charcoal/30 px-5 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-charcoal hover:text-bone transition-colors">
                  <Download size={14} /> Export CSV
                </button>
                <button onClick={() => { resetBulk(); setBulkPreview(false); setBulkOpen(true); }} data-testid="bulk-email-btn"
                  className="inline-flex items-center gap-2 border border-charcoal/30 px-5 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-charcoal hover:text-bone transition-colors">
                  <Mail size={14} /> Announcement
                </button>
                <button onClick={() => setAddOpen(true)} data-testid="add-member-btn"
                  className="inline-flex items-center gap-2 bg-charcoal text-bone px-5 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors">
                  <UserPlus size={14} /> Add Member
                </button>
              </div>
            </div>
            <div className="border border-charcoal/10 overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-sand font-mono text-[10px] uppercase tracking-widest text-charcoal/60">
                    <th className="px-5 py-3">Name</th>
                    <th className="px-5 py-3 hidden sm:table-cell">Email</th>
                    <th className="px-5 py-3">Given</th>
                    <th className="px-5 py-3">Role</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.id} className="border-t border-charcoal/10" data-testid={`user-row-${u.id}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <span className="font-serif text-lg">{u.name}</span>
                          {u.verified === false && (
                            <span data-testid={`unverified-badge-${u.id}`} title="Awaiting email verification"
                              className="inline-flex items-center gap-1 border border-gold/50 bg-gold/15 text-charcoal/80 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest">
                              <Clock size={10} /> Unverified
                            </span>
                          )}
                        </div>
                        <div className="font-mono text-[10px] text-charcoal/40 sm:hidden">{u.email}</div>
                      </td>
                      <td className="px-5 py-4 hidden sm:table-cell font-sans text-sm text-charcoal/70">{u.email}</td>
                      <td className="px-5 py-4 font-mono text-sm">{u.total_given > 0 ? `$${Number(u.total_given).toLocaleString()}` : "—"}</td>
                      <td className="px-5 py-4">
                        <select value={u.role} onChange={(e) => changeRole(u.id, e.target.value)} data-testid={`role-select-${u.id}`}
                          className="border border-charcoal/20 bg-transparent px-3 py-1.5 font-mono text-xs uppercase tracking-widest focus:outline-none focus:border-olive">
                          <option value="member">Member</option>
                          <option value="supporter">Supporter</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => { setEmailUser(u); setEmailForm({ subject: "", message: "" }); }} data-testid={`email-user-${u.id}`} title="Email"
                            className="p-2 border border-charcoal/20 hover:bg-olive hover:text-bone transition-colors">
                            <Mail size={14} />
                          </button>
                          <button onClick={() => setEditUser({ ...u })} data-testid={`edit-user-${u.id}`} title="Edit"
                            className="p-2 border border-charcoal/20 hover:bg-navy hover:text-bone transition-colors">
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => setDeleteUser(u)} disabled={u.id === me?.id} data-testid={`delete-user-${u.id}`} title={u.id === me?.id ? "You cannot delete yourself" : "Delete"}
                            className="p-2 border border-charcoal/20 hover:bg-rust hover:text-bone transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-charcoal">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr><td colSpan={5} className="px-5 py-8 font-sans text-charcoal/50">No users match your search.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>

        {/* ACTIVITY LOG */}
        <Reveal delay={0.1}>
          <div className="mt-16" data-testid="admin-announcements">
            <div className="flex items-center gap-3 mb-6">
              <Mail size={20} className="text-olive" strokeWidth={1.5} />
              <h2 className="font-serif text-3xl">Saved Announcements</h2>
            </div>
            <div className="border border-charcoal/10 divide-y divide-charcoal/10">
              {announcements.map((a) => (
                <div key={a.id} className="flex items-start justify-between gap-4 px-5 py-4" data-testid={`announcement-${a.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-mono text-[10px] uppercase tracking-widest ${a.status === "scheduled" ? "text-navy" : a.status === "sent" ? "text-olive" : "text-charcoal/50"}`}>{a.status}</span>
                      <span className="font-mono text-[10px] text-charcoal/40">· {a.audience}</span>
                      {a.status === "scheduled" && a.scheduled_at && <span className="font-mono text-[10px] text-navy">· {new Date(a.scheduled_at).toLocaleString()}</span>}
                      {a.recurrence && a.recurrence !== "none" && <span className="font-mono text-[10px] text-gold">· repeats {a.recurrence}</span>}
                      {a.status === "sent" && a.sent_at && <span className="font-mono text-[10px] text-charcoal/40">· sent to {a.sent_count} {new Date(a.sent_at).toLocaleDateString()}</span>}
                      {a.status === "sent" && <span className="font-mono text-[10px] text-olive" data-testid={`announcement-opens-${a.id}`}>· {a.open_count || 0} opened</span>}
                    </div>
                    <p className="font-serif text-lg mt-1 truncate">{a.subject}</p>
                    <p className="font-sans text-sm text-charcoal/55 mt-0.5 line-clamp-1">{a.message}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {a.status !== "sent" && (
                      <button onClick={() => sendAnnouncementNow(a)} disabled={busy} data-testid={`announcement-send-${a.id}`} title="Send now"
                        className="p-2 border border-charcoal/20 hover:bg-olive hover:text-bone transition-colors"><Mail size={14} /></button>
                    )}
                    <button onClick={() => loadAnnouncement(a)} data-testid={`announcement-edit-${a.id}`} title="Edit"
                      className="p-2 border border-charcoal/20 hover:bg-navy hover:text-bone transition-colors"><Pencil size={14} /></button>
                    <button onClick={() => deleteAnnouncement(a.id)} data-testid={`announcement-delete-${a.id}`} title="Delete"
                      className="p-2 border border-charcoal/20 hover:bg-rust hover:text-bone transition-colors"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
              {announcements.length === 0 && <div className="px-5 py-8 font-sans text-charcoal/50">No saved announcements yet. Use the "Announcement" button to draft or schedule one.</div>}
            </div>
          </div>
        </Reveal>

        {/* ADMIN ACTIVITY LOG */}
        <Reveal delay={0.1}>
          <div className="mt-16" data-testid="admin-activity">
            <div className="flex items-center gap-3 mb-6">
              <Clock size={20} className="text-olive" strokeWidth={1.5} />
              <h2 className="font-serif text-3xl">Activity Log</h2>
            </div>
            <div className="border border-charcoal/10 divide-y divide-charcoal/10">
              {activity.map((a) => (
                <div key={a.id} className="flex items-start justify-between gap-4 px-5 py-4" data-testid={`activity-${a.id}`}>
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-gold">{a.action.replace(/_/g, " ")}</span>
                    <p className="font-sans text-sm text-charcoal/80 mt-1">{a.detail}</p>
                    <p className="font-mono text-[10px] text-charcoal/40 mt-1">by {a.admin_name}</p>
                  </div>
                  <span className="font-mono text-[10px] text-charcoal/40 shrink-0">{new Date(a.created_at).toLocaleString()}</span>
                </div>
              ))}
              {activity.length === 0 && <div className="px-5 py-8 font-sans text-charcoal/50">No admin activity yet.</div>}
            </div>
          </div>
        </Reveal>


        <Reveal delay={0.1}>
          <form onSubmit={save} className="mt-16 border border-charcoal/10 p-8 max-w-lg" data-testid="settings-form">
            <h2 className="font-serif text-2xl mb-6">Notification Email</h2>
            <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">Inquiries are sent to</label>
            <input type="email" value={settings.notify_email || ""} onChange={(e) => setSettings({ ...settings, notify_email: e.target.value })} required data-testid="notify-email"
              className="w-full border-b border-charcoal/30 bg-transparent py-3 font-sans focus:outline-none focus:border-olive" />
            <button disabled={saving} data-testid="settings-save" className="mt-6 bg-charcoal text-bone px-10 py-3 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors disabled:opacity-50">{saving ? "Saving..." : "Save"}</button>
          </form>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="mt-10 border border-charcoal/10 p-8 max-w-lg" data-testid="reminder-timing">
            <h2 className="font-serif text-2xl mb-3">Event Reminder Timing</h2>
            <p className="font-sans text-sm text-charcoal/60 mb-5">Choose how far ahead ticket holders get their reminder email with QR code.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[{ v: 0, label: "Day of event" }, { v: 1, label: "Night before" }, { v: 7, label: "One week early" }].map((o) => {
                const active = Number(settings.reminder_lead_days || 0) === o.v;
                return (
                  <button key={o.v} type="button" data-testid={`lead-${o.v}`}
                    onClick={() => { if (!settings.notify_email) { toast.error("Settings still loading, try again in a moment"); return; } setSettings((s) => ({ ...s, reminder_lead_days: o.v })); api.put("/settings", { notify_email: settings.notify_email, reminder_lead_days: o.v }).then(() => toast.success("Reminder timing updated")).catch(() => toast.error("Could not save")); }}
                    className={`px-4 py-4 font-mono text-xs uppercase tracking-widest border transition-colors ${active ? "bg-navy text-bone border-navy" : "border-charcoal/20 hover:border-navy"}`}>
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.13}>
          <div className="mt-10 border border-charcoal/10 p-8 max-w-lg" data-testid="marquee-editor">
            <h2 className="font-serif text-2xl mb-3">Homepage Scrolling Marquee</h2>
            <p className="font-sans text-sm text-charcoal/60 mb-5">The words that scroll across the homepage. Separate each with a comma.</p>
            <textarea value={marqueeText} onChange={(e) => setMarqueeText(e.target.value)} rows={3} data-testid="marquee-input"
              placeholder="Physical, Emotional, Spiritual, Brotherhood"
              className="w-full border border-charcoal/20 bg-transparent p-3 font-sans text-sm focus:outline-none focus:border-olive resize-none" />
            <button disabled={savingMarquee} onClick={saveMarquee} data-testid="marquee-save"
              className="mt-4 bg-charcoal text-bone px-10 py-3 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors disabled:opacity-50">{savingMarquee ? "Saving..." : "Save Marquee"}</button>
          </div>
        </Reveal>

        <Reveal delay={0.13}>
          <div className="mt-10 border border-charcoal/10 p-8 max-w-lg" data-testid="wod-editor">
            <div className="flex items-center justify-between gap-4 mb-2">
              <h2 className="font-serif text-2xl">Word of the Day</h2>
              <button type="button" onClick={() => setWod({ ...wod, enabled: !wod.enabled })} data-testid="wod-toggle"
                className={`relative w-12 h-6 rounded-full transition-colors ${wod.enabled ? "bg-navy" : "bg-charcoal/25"}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-bone rounded-full transition-transform ${wod.enabled ? "translate-x-6" : ""}`} />
              </button>
            </div>
            <p className="font-sans text-sm text-charcoal/60 mb-5">Fades in for members when they log in. Toggle on to show it.</p>
            <div className="flex gap-2 mb-6" data-testid="wod-mode">
              {[{ k: "manual", t: "Manual" }, { k: "auto", t: "Automatic" }].map((o) => (
                <button type="button" key={o.k} onClick={() => setWod({ ...wod, mode: o.k })} data-testid={`wod-mode-${o.k}`}
                  className={`flex-1 px-4 py-2.5 font-mono text-xs uppercase tracking-widest border transition-colors ${wod.mode === o.k ? "bg-navy text-bone border-navy" : "border-charcoal/20 hover:border-navy"}`}>
                  {o.t}
                </button>
              ))}
            </div>
            <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">Narration voice</label>
            <div className="flex gap-2 mt-2 mb-6">
              <select value={wod.voice} onChange={(e) => setWod({ ...wod, voice: e.target.value })} data-testid="wod-voice"
                className="flex-1 border border-charcoal/20 bg-transparent px-3 py-2.5 font-mono text-xs uppercase tracking-widest focus:outline-none focus:border-olive">
                <option value="onyx">Onyx — deep, authoritative</option>
                <option value="echo">Echo — smooth, calm</option>
                <option value="sage">Sage — wise, measured</option>
                <option value="coral">Coral — warm, friendly</option>
                <option value="fable">Fable — expressive, storytelling</option>
                <option value="alloy">Alloy — neutral, balanced</option>
                <option value="ash">Ash — clear, articulate</option>
                <option value="nova">Nova — energetic, upbeat</option>
                <option value="shimmer">Shimmer — bright, cheerful</option>
              </select>
              <button type="button" onClick={previewVoice} disabled={previewingVoice} data-testid="wod-voice-preview"
                className="inline-flex items-center gap-2 border border-charcoal/30 px-5 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-navy hover:text-bone hover:border-navy transition-colors disabled:opacity-50 shrink-0">
                {previewingVoice ? <Loader2 size={14} className="animate-spin" /> : <Volume2 size={14} />} Preview
              </button>
            </div>
            {wod.mode === "auto" ? (
              <>
                <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">Rotate daily from</label>
                <select value={wod.auto_source} onChange={(e) => setWod({ ...wod, auto_source: e.target.value })} data-testid="wod-source"
                  className="w-full border border-charcoal/20 bg-transparent px-3 py-2.5 mt-2 mb-5 font-mono text-xs uppercase tracking-widest focus:outline-none focus:border-olive">
                  <option value="verses">Built-in Scripture verses</option>
                  <option value="devotionals">Your devotionals</option>
                </select>
                <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">Heading</label>
                <input value={wod.heading || ""} onChange={(e) => setWod({ ...wod, heading: e.target.value })} data-testid="wod-heading"
                  placeholder="Word of the Day"
                  className="w-full border-b border-charcoal/30 bg-transparent py-2.5 font-sans focus:outline-none focus:border-olive mb-2" />
                <p className="font-sans text-xs text-charcoal/50 mb-2">A fresh verse is chosen automatically each day — no manual updates needed.</p>
                {wod.auto_source === "verses" && (
                  <div className="mt-6 border-t border-charcoal/10 pt-6" data-testid="verse-library">
                    <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/70 mb-1">Verse Library</h3>
                    <p className="font-sans text-xs text-charcoal/50 mb-4">Add your own verses to the rotation. When you have your own, the daily verse is drawn from this list instead of the built-in set.</p>
                    <div className="space-y-2 mb-4">
                      {verses.length === 0 && <p className="font-sans text-sm text-charcoal/40">No custom verses yet — the built-in set is used.</p>}
                      {verses.length > 1 && <p className="font-sans text-[11px] text-charcoal/40 mb-1">Drag to reorder the rotation sequence.</p>}
                      {verses.map((v, i) => (
                        <div key={v.id} data-testid={`verse-${v.id}`}
                          draggable
                          onDragStart={() => setDragIdx(i)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => dropVerse(i)}
                          onDragEnd={() => setDragIdx(null)}
                          className={`flex items-start gap-2 border border-charcoal/10 p-3 bg-bone transition-opacity ${dragIdx === i ? "opacity-40" : ""}`}>
                          <GripVertical size={15} className="text-charcoal/30 mt-0.5 cursor-grab shrink-0" />
                          <div className="flex-1">
                            {v.scripture && <span className="font-mono text-[10px] uppercase tracking-widest text-gold block">{v.scripture}</span>}
                            <span className="font-serif italic text-charcoal/85 text-sm">{v.message}</span>
                          </div>
                          <button onClick={() => deleteVerse(v.id)} data-testid={`delete-verse-${v.id}`} title="Remove"
                            className="p-1.5 border border-charcoal/20 hover:bg-rust hover:text-bone transition-colors shrink-0"><Trash2 size={13} /></button>
                        </div>
                      ))}
                    </div>
                    <input value={newVerse.scripture} onChange={(e) => setNewVerse({ ...newVerse, scripture: e.target.value })} data-testid="new-verse-scripture"
                      placeholder="Scripture reference (e.g. Psalm 23:1)"
                      className="w-full border-b border-charcoal/30 bg-transparent py-2 font-sans text-sm focus:outline-none focus:border-olive mb-3" />
                    <textarea value={newVerse.message} onChange={(e) => setNewVerse({ ...newVerse, message: e.target.value })} rows={2} data-testid="new-verse-message"
                      placeholder="The verse text..."
                      className="w-full border border-charcoal/20 bg-transparent p-3 font-sans text-sm focus:outline-none focus:border-olive resize-none" />
                    <button disabled={addingVerse} onClick={addVerse} data-testid="add-verse-btn"
                      className="mt-3 border border-charcoal/30 px-6 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-charcoal hover:text-bone transition-colors disabled:opacity-50">{addingVerse ? "Adding..." : "Add Verse"}</button>
                  </div>
                )}
              </>
            ) : (
              <>
                <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">Heading</label>
                <input value={wod.heading || ""} onChange={(e) => setWod({ ...wod, heading: e.target.value })} data-testid="wod-heading"
                  placeholder="Word of the Day"
                  className="w-full border-b border-charcoal/30 bg-transparent py-2.5 font-sans focus:outline-none focus:border-olive mb-5" />
                <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">Scripture reference</label>
                <input value={wod.scripture || ""} onChange={(e) => setWod({ ...wod, scripture: e.target.value })} data-testid="wod-scripture"
                  placeholder="Proverbs 27:17"
                  className="w-full border-b border-charcoal/30 bg-transparent py-2.5 font-sans focus:outline-none focus:border-olive mb-5" />
                <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">Message</label>
                <textarea value={wod.message || ""} onChange={(e) => setWod({ ...wod, message: e.target.value })} rows={3} data-testid="wod-message"
                  placeholder="As iron sharpens iron, so one man sharpens another."
                  className="w-full border border-charcoal/20 bg-transparent p-3 mt-1 font-sans text-sm focus:outline-none focus:border-olive resize-none" />
              </>
            )}
            <button disabled={savingWod} onClick={saveWod} data-testid="wod-save"
              className="mt-4 bg-charcoal text-bone px-10 py-3 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors disabled:opacity-50">{savingWod ? "Saving..." : "Save Word of the Day"}</button>
          </div>
        </Reveal>

        <Reveal delay={0.13}>
          <div className="mt-10 border border-charcoal/10 p-8 max-w-lg" data-testid="radio-editor">
            <div className="flex items-center justify-between gap-4 mb-2">
              <div className="flex items-center gap-3"><Radio size={20} className="text-rust" strokeWidth={1.6} /><h2 className="font-serif text-2xl">IHIMM Radio</h2></div>
              <button type="button" onClick={() => setRadio({ ...radio, enabled: !radio.enabled })} data-testid="radio-toggle"
                className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${radio.enabled ? "bg-navy" : "bg-charcoal/25"}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-bone rounded-full transition-transform ${radio.enabled ? "translate-x-6" : ""}`} />
              </button>
            </div>
            <p className="font-sans text-sm text-charcoal/60 mb-5">A floating player visitors can tap to listen while they browse. Upload tracks for your own continuous station, or point to an online stream.</p>
            <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">Station name</label>
            <input value={radio.station_name || ""} onChange={(e) => setRadio({ ...radio, station_name: e.target.value })} data-testid="radio-station-name"
              className="w-full border-b border-charcoal/30 bg-transparent py-2.5 font-sans focus:outline-none focus:border-olive mb-5" />
            <div className="flex gap-2 mb-5" data-testid="radio-mode">
              {[{ k: "playlist", t: "My Playlist" }, { k: "external", t: "Online Station" }].map((o) => (
                <button type="button" key={o.k} onClick={() => setRadio({ ...radio, mode: o.k })} data-testid={`radio-mode-${o.k}`}
                  className={`flex-1 px-4 py-2.5 font-mono text-xs uppercase tracking-widest border transition-colors ${radio.mode === o.k ? "bg-navy text-bone border-navy" : "border-charcoal/20 hover:border-navy"}`}>
                  {o.t}
                </button>
              ))}
            </div>
            {radio.mode === "external" ? (
              <>
                <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">Stream URL</label>
                <input value={radio.external_url || ""} onChange={(e) => setRadio({ ...radio, external_url: e.target.value })} data-testid="radio-external-url"
                  placeholder="https://stream.example.com/live.mp3"
                  className="w-full border-b border-charcoal/30 bg-transparent py-2.5 font-sans text-sm focus:outline-none focus:border-olive mb-4" />
                <label className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">Station label</label>
                <input value={radio.external_label || ""} onChange={(e) => setRadio({ ...radio, external_label: e.target.value })} data-testid="radio-external-label"
                  placeholder="e.g. Praise FM Live"
                  className="w-full border-b border-charcoal/30 bg-transparent py-2.5 font-sans text-sm focus:outline-none focus:border-olive mb-2" />
                <p className="font-sans text-xs text-charcoal/50">Paste a direct audio stream link (MP3/AAC). Most internet radio stations publish one.</p>
              </>
            ) : (
              <div data-testid="radio-track-manager">
                <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/70 mb-1">Station Playlist</h3>
                <p className="font-sans text-xs text-charcoal/50 mb-4">Tracks loop continuously like a live station — every listener hears the same moment.</p>
                <div className="space-y-2 mb-1">
                  {(radio.tracks || []).length === 0 && <p className="font-sans text-sm text-charcoal/40">No tracks yet — upload your first one below.</p>}
                  {(radio.tracks || []).map((t, i) => (
                    <div key={t.id} data-testid={`radio-track-${t.id}`} draggable
                      onDragStart={(e) => { e.dataTransfer.setData("text/plain", t.id); e.dataTransfer.effectAllowed = "move"; setTrackDragIdx(i); }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => dropTrack(i)}
                      className={`flex items-center gap-2 border border-charcoal/10 p-3 bg-bone transition-opacity ${trackDragIdx === i ? "opacity-40" : ""}`}>
                      <span className="cursor-grab active:cursor-grabbing text-charcoal/30 shrink-0" title="Drag to reorder"><GripVertical size={14} /></span>
                      <Music size={15} className="text-charcoal/40 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="font-sans text-sm text-charcoal/85 block truncate">{t.title}</span>
                        <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/40">{fmtDur(t.duration)}</span>
                      </div>
                      <select value={t.category || "general"} onChange={(e) => setTrackCategory(t.id, e.target.value)} data-testid={`track-category-${t.id}`}
                        className="border border-charcoal/20 bg-transparent px-1.5 py-1 font-mono text-[10px] uppercase focus:outline-none shrink-0">
                        {RADIO_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <button onClick={() => deleteTrack(t.id)} data-testid={`delete-track-${t.id}`} title="Remove"
                        className="p-1.5 border border-charcoal/20 hover:bg-rust hover:text-bone transition-colors shrink-0"><Trash2 size={13} /></button>
                    </div>
                  ))}
                </div>
                {(radio.tracks || []).length > 1 && <p className="font-sans text-[11px] text-charcoal/40 mb-4">Drag to reorder the playback sequence.</p>}
                <div className="flex items-center gap-3 flex-wrap mt-3">
                  <input type="file" accept=".mp3,.m4a,.wav,.ogg,.aac,audio/*" ref={trackFileRef} onChange={uploadTrack} className="hidden" data-testid="radio-upload-input" />
                  <button type="button" disabled={uploadingTrack} onClick={() => trackFileRef.current?.click()} data-testid="radio-upload-btn"
                    className="inline-flex items-center gap-2 border border-charcoal/30 px-6 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-charcoal hover:text-bone transition-colors disabled:opacity-50">
                    {uploadingTrack ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {uploadingTrack ? "Uploading..." : "Upload Track"}
                  </button>
                  <select value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)} data-testid="radio-upload-category" title="Category for new upload"
                    className="border border-charcoal/20 bg-transparent px-2 py-2.5 font-mono text-[10px] uppercase focus:outline-none">
                    {RADIO_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <p className="font-sans text-[11px] text-charcoal/40 mt-2">MP3, M4A, WAV, OGG or AAC · up to 30MB per track</p>
                <div className="mt-8 pt-6 border-t border-charcoal/10" data-testid="radio-schedule">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/70">Broadcast Schedule</h3>
                      <p className="font-sans text-xs text-charcoal/50 mt-1">Play different content by time of day — e.g. worship in the morning, sermons in the evening.</p>
                    </div>
                    <button type="button" onClick={() => setRadio({ ...radio, schedule_enabled: !radio.schedule_enabled })} data-testid="radio-schedule-toggle"
                      className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${radio.schedule_enabled ? "bg-navy" : "bg-charcoal/25"}`}>
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-bone rounded-full transition-transform ${radio.schedule_enabled ? "translate-x-6" : ""}`} />
                    </button>
                  </div>
                  {radio.schedule_enabled && (
                    <div className="mt-4">
                      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/50">Station time zone</label>
                      <select value={radio.timezone || "America/New_York"} onChange={(e) => setRadio({ ...radio, timezone: e.target.value })} data-testid="radio-timezone"
                        className="w-full border border-charcoal/20 bg-transparent px-3 py-2 mt-1 mb-4 font-mono text-xs focus:outline-none focus:border-olive">
                        {TZ_OPTIONS.map((tz) => <option key={tz} value={tz}>{tz.replace("America/", "").replace("_", " ")}</option>)}
                      </select>
                      <div className="space-y-2 mb-3">
                        {(radio.blocks || []).length === 0 && <p className="font-sans text-sm text-charcoal/40">No blocks yet. Outside of blocks, the full playlist plays.</p>}
                        {(radio.blocks || []).map((b, i) => (
                          <div key={b.id || i} data-testid={`radio-block-${i}`} className="border border-charcoal/10 p-3 bg-bone space-y-2">
                            <div className="flex items-center gap-2">
                              <input value={b.label || ""} placeholder="Block name (e.g. Morning Worship)"
                                onChange={(e) => setRadio({ ...radio, blocks: radio.blocks.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })}
                                data-testid={`block-label-${i}`}
                                className="flex-1 min-w-0 border-b border-charcoal/20 bg-transparent py-1 font-sans text-sm focus:outline-none focus:border-olive" />
                              <button onClick={() => setRadio({ ...radio, blocks: radio.blocks.filter((_, j) => j !== i) })} data-testid={`delete-block-${i}`} title="Remove block"
                                className="p-1.5 border border-charcoal/20 hover:bg-rust hover:text-bone transition-colors shrink-0"><Trash2 size={12} /></button>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <select value={b.start_hour ?? 6} onChange={(e) => setRadio({ ...radio, blocks: radio.blocks.map((x, j) => (j === i ? { ...x, start_hour: Number(e.target.value) } : x)) })}
                                data-testid={`block-start-${i}`} className="border border-charcoal/20 bg-transparent px-2 py-1.5 font-mono text-xs focus:outline-none">
                                {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{fmtHour(h)}</option>)}
                              </select>
                              <span className="font-mono text-[10px] text-charcoal/40 uppercase">to</span>
                              <select value={b.end_hour ?? 12} onChange={(e) => setRadio({ ...radio, blocks: radio.blocks.map((x, j) => (j === i ? { ...x, end_hour: Number(e.target.value) } : x)) })}
                                data-testid={`block-end-${i}`} className="border border-charcoal/20 bg-transparent px-2 py-1.5 font-mono text-xs focus:outline-none">
                                {Array.from({ length: 24 }, (_, h) => <option key={h + 1} value={h + 1}>{fmtHour(h + 1)}</option>)}
                              </select>
                              <select value={b.category || "general"} onChange={(e) => setRadio({ ...radio, blocks: radio.blocks.map((x, j) => (j === i ? { ...x, category: e.target.value } : x)) })}
                                data-testid={`block-category-${i}`} className="border border-charcoal/20 bg-transparent px-2 py-1.5 font-mono text-xs uppercase focus:outline-none">
                                {RADIO_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                              </select>
                            </div>
                          </div>
                        ))}
                      </div>
                      <button type="button" onClick={() => setRadio({ ...radio, blocks: [...(radio.blocks || []), { id: crypto.randomUUID(), label: "", start_hour: 6, end_hour: 12, category: "worship" }] })}
                        data-testid="radio-add-block"
                        className="border border-charcoal/30 px-5 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-charcoal hover:text-bone transition-colors">+ Add Block</button>
                      <p className="font-sans text-[11px] text-charcoal/40 mt-2">During a block, only tracks in its category play. Remember to hit "Save Radio Settings".</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            <button disabled={savingRadio} onClick={saveRadio} data-testid="radio-save"
              className="mt-6 block bg-charcoal text-bone px-10 py-3 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors disabled:opacity-50">{savingRadio ? "Saving..." : "Save Radio Settings"}</button>
          </div>
        </Reveal>

        <Reveal delay={0.14}>
          <div className="mt-10 border border-charcoal/10 p-8 max-w-2xl" data-testid="email-preview-card">
            <div className="flex items-center gap-3 mb-2"><Eye size={20} className="text-navy" strokeWidth={1.6} /><h2 className="font-serif text-2xl">Automated Email Previews</h2></div>
            <p className="font-sans text-sm text-charcoal/60 mb-5">See exactly what members receive before these automated emails go out.</p>
            <select value={previewKind} onChange={(e) => setPreviewKind(e.target.value)} data-testid="email-preview-select"
              className="border border-charcoal/20 bg-transparent px-3 py-2.5 font-mono text-xs uppercase tracking-widest focus:outline-none focus:border-olive w-full sm:w-auto">
              {emailTypes.map((t) => <option key={t.kind} value={t.kind}>{t.label}</option>)}
            </select>
            {previewSubject && (
              <p className="mt-5 font-mono text-[10px] uppercase tracking-widest text-charcoal/50" data-testid="email-preview-subject">
                Subject: <span className="text-charcoal/80 normal-case tracking-normal font-sans text-sm">{previewSubject}</span>
              </p>
            )}
            <div className="mt-3 border border-charcoal/10 bg-white" data-testid="email-preview-frame">
              {previewLoading
                ? <div className="h-40 flex items-center justify-center font-sans text-sm text-charcoal/40">Loading preview…</div>
                : <iframe title="email-preview" srcDoc={previewHtml} className="w-full h-[520px]" sandbox="" />}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-10 border border-charcoal/10 p-8 max-w-lg">
            <h2 className="font-serif text-2xl mb-3">Daily Devotional Email</h2>
            <p className="font-sans text-sm text-charcoal/60 mb-6">Automatically sent to opted-in members every morning. Send today's edition now.</p>
            <button onClick={sendDaily} disabled={sendingDaily} data-testid="send-daily-btn"
              className="bg-gold text-charcoal px-10 py-3 font-mono text-xs uppercase tracking-widest hover:bg-gold/80 transition-colors disabled:opacity-50">
              {sendingDaily ? "Sending..." : "Send Today's Word"}
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.18}>
          <Link to="/admin/scanner" data-testid="admin-scanner-link"
            className="group mt-10 border border-charcoal/10 p-8 max-w-lg flex items-center justify-between hover:bg-navy hover:text-bone transition-colors duration-300">
            <div className="flex items-center gap-4">
              <QrCode size={26} strokeWidth={1.4} className="text-gold" />
              <div>
                <h2 className="font-serif text-2xl">Check-In Scanner</h2>
                <p className="font-sans text-sm opacity-70 mt-1">Scan ticket QR codes to confirm entry at events.</p>
              </div>
            </div>
            <ArrowUpRight size={20} className="hover-arrow shrink-0" />
          </Link>
        </Reveal>

        <div className="mt-20">
          <h2 className="font-serif text-3xl mb-8">Inquiries ({inquiries.length})</h2>
          <div className="space-y-px bg-charcoal/10 border border-charcoal/10">
            {inquiries.map((q) => (
              <div key={q.id} className="bg-bone p-8" data-testid={`inquiry-${q.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="font-mono text-xs uppercase tracking-widest text-gold">{q.type}</span>
                  <span className="font-mono text-xs text-charcoal/40">{new Date(q.created_at).toLocaleDateString()}</span>
                </div>
                <h3 className="font-serif text-2xl mt-2">{q.subject}</h3>
                <p className="font-sans text-charcoal/70 mt-3">{q.message}</p>
                <p className="font-mono text-xs text-charcoal/50 mt-4">{q.name} · {q.email}{q.organization ? ` · ${q.organization}` : ""}</p>
              </div>
            ))}
            {inquiries.length === 0 && <div className="bg-bone p-8 font-sans text-charcoal/50">No inquiries yet.</div>}
          </div>
        </div>
      </div>

      {/* ADD MEMBER MODAL */}
      {addOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-testid="add-member-modal">
          <div className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm" onClick={() => !busy && setAddOpen(false)} />
          <form onSubmit={addMember} className="relative bg-bone w-full max-w-md p-8 border border-charcoal/20">
            <button type="button" onClick={() => setAddOpen(false)} data-testid="add-member-close" className="absolute top-4 right-4 text-charcoal/40 hover:text-rust"><X size={18} /></button>
            <h3 className="font-serif text-3xl tracking-tight">Add Member</h3>
            <p className="font-sans text-sm text-charcoal/55 mt-1">Create an account manually. Share the password with them so they can sign in.</p>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60 mt-6">Name</label>
            <input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} required data-testid="add-member-name"
              className="w-full border-b border-charcoal/30 bg-transparent py-2 font-sans focus:outline-none focus:border-olive" />
            <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60 mt-5">Email</label>
            <input type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} required data-testid="add-member-email"
              className="w-full border-b border-charcoal/30 bg-transparent py-2 font-sans focus:outline-none focus:border-olive" />
            <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60 mt-5">Temporary Password</label>
            <input type="text" value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} required minLength={6} data-testid="add-member-password"
              className="w-full border-b border-charcoal/30 bg-transparent py-2 font-sans focus:outline-none focus:border-olive" />
            <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60 mt-5">Role</label>
            <select value={addForm.role} onChange={(e) => setAddForm({ ...addForm, role: e.target.value })} data-testid="add-member-role"
              className="w-full border border-charcoal/20 bg-transparent px-3 py-2.5 mt-1 font-mono text-xs uppercase tracking-widest focus:outline-none focus:border-olive">
              <option value="member">Member</option>
              <option value="supporter">Supporter</option>
              <option value="admin">Admin</option>
            </select>
            <label className="flex items-center gap-3 mt-5 font-sans text-sm cursor-pointer">
              <input type="checkbox" checked={addForm.send_welcome} onChange={(e) => setAddForm({ ...addForm, send_welcome: e.target.checked })} data-testid="add-member-welcome" className="w-4 h-4 accent-navy" />
              Email them a welcome note with their login details
            </label>
            <button disabled={busy} data-testid="add-member-save" className="mt-8 w-full bg-charcoal text-bone py-3 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors disabled:opacity-50">
              {busy ? "Creating..." : "Create Member"}
            </button>
          </form>
        </div>
      )}

      {/* BULK EMAIL MODAL */}
      {bulkOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 overflow-y-auto" data-testid="bulk-email-modal">
          <div className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm" onClick={() => !busy && setBulkOpen(false)} />
          <form onSubmit={sendBulk} className="relative bg-bone w-full max-w-md p-8 border border-charcoal/20 my-8">
            <button type="button" onClick={() => setBulkOpen(false)} data-testid="bulk-email-close" className="absolute top-4 right-4 text-charcoal/40 hover:text-rust"><X size={18} /></button>
            <h3 className="font-serif text-3xl tracking-tight">{bulkForm.id ? "Announcement" : "Send Announcement"}</h3>
            <p className="font-sans text-sm text-charcoal/55 mt-1">Email an announcement or newsletter to your community.</p>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60 mt-6">Send To</label>
            <select value={bulkForm.audience} onChange={(e) => setBulkForm({ ...bulkForm, audience: e.target.value })} data-testid="bulk-email-audience"
              className="w-full border border-charcoal/20 bg-transparent px-3 py-2.5 mt-1 font-mono text-xs uppercase tracking-widest focus:outline-none focus:border-olive">
              <option value="all">Everyone</option>
              <option value="members">Members (non-admin)</option>
              <option value="supporters">Supporters only</option>
            </select>
            <p className="font-sans text-xs text-olive mt-2" data-testid="bulk-email-count">
              {audienceCount === null ? "Counting recipients…" : `This will reach ${audienceCount} ${audienceCount === 1 ? "person" : "people"}.`}
            </p>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60 mt-5">Start From a Template <span className="text-charcoal/35 normal-case tracking-normal">(optional)</span></label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="newsletter-templates">
              {NEWSLETTER_TEMPLATES.map((t) => (
                <button key={t.id} type="button" onClick={() => setBulkForm((f) => ({ ...f, subject: t.subject, message: t.message }))}
                  data-testid={`template-${t.id}`} className="px-3 py-1.5 border border-charcoal/25 font-mono text-[10px] uppercase tracking-widest hover:bg-navy hover:text-bone transition-colors">
                  {t.label}
                </button>
              ))}
              {customTemplates.map((t) => (
                <span key={t.id} className="group inline-flex items-center gap-1 border border-olive/50 bg-olive/5" data-testid={`custom-template-${t.id}`}>
                  <button type="button" onClick={() => setBulkForm((f) => ({ ...f, subject: t.subject, message: t.message }))}
                    data-testid={`use-template-${t.id}`} className="pl-3 pr-1 py-1.5 font-mono text-[10px] uppercase tracking-widest text-olive hover:text-charcoal transition-colors">
                    {t.name}
                  </button>
                  <button type="button" onClick={() => deleteTemplate(t.id)} data-testid={`delete-template-${t.id}`} title="Delete template"
                    className="pr-2 pl-0.5 py-1.5 text-olive/60 hover:text-rust transition-colors"><X size={11} /></button>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Save current as template…" data-testid="template-name-input"
                className="flex-1 border-b border-charcoal/25 bg-transparent py-1.5 font-sans text-sm focus:outline-none focus:border-olive" />
              <button type="button" onClick={saveTemplate} disabled={tplSaving} data-testid="save-template-btn"
                className="px-4 py-1.5 border border-charcoal/30 font-mono text-[10px] uppercase tracking-widest hover:bg-charcoal hover:text-bone transition-colors disabled:opacity-50">
                {tplSaving ? "Saving…" : "Save"}
              </button>
            </div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60 mt-5">Subject</label>
            <input value={bulkForm.subject} onChange={(e) => setBulkForm({ ...bulkForm, subject: e.target.value })} required data-testid="bulk-email-subject"
              className="w-full border-b border-charcoal/30 bg-transparent py-2 font-sans focus:outline-none focus:border-olive" />
            <div className="flex items-center justify-between mt-5">
              <label className="font-mono text-[10px] uppercase tracking-widest text-charcoal/60">Message</label>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => insertFormat("heading")} data-testid="format-heading" title="Heading" className="p-1.5 border border-charcoal/20 hover:bg-navy hover:text-bone transition-colors"><Heading size={13} /></button>
                <button type="button" onClick={() => insertFormat("bold")} data-testid="format-bold" title="Bold" className="p-1.5 border border-charcoal/20 hover:bg-navy hover:text-bone transition-colors"><Bold size={13} /></button>
                <button type="button" onClick={() => insertFormat("link")} data-testid="format-link" title="Link" className="p-1.5 border border-charcoal/20 hover:bg-navy hover:text-bone transition-colors"><Link2 size={13} /></button>
                <button type="button" onClick={() => setBulkPreview((p) => !p)} data-testid="format-preview" title="Preview" className={`p-1.5 border border-charcoal/20 transition-colors ${bulkPreview ? "bg-olive text-bone" : "hover:bg-navy hover:text-bone"}`}><Eye size={13} /></button>
              </div>
            </div>
            {bulkPreview ? (
              <div className="w-full min-h-[132px] border border-charcoal/20 p-3 mt-1 bg-white font-sans text-sm text-charcoal/85" data-testid="bulk-email-preview" dangerouslySetInnerHTML={{ __html: formatMessage(bulkForm.message) || "<span style='color:#999'>Nothing to preview yet.</span>" }} />
            ) : (
              <textarea ref={msgRef} value={bulkForm.message} onChange={(e) => setBulkForm({ ...bulkForm, message: e.target.value })} required rows={6} data-testid="bulk-email-message"
                className="w-full border border-charcoal/20 bg-transparent p-3 mt-1 font-sans text-sm focus:outline-none focus:border-olive resize-none" />
            )}
            <p className="font-sans text-[11px] text-charcoal/40 mt-1">Formatting: <span className="font-mono">## Heading</span>, <span className="font-mono">**bold**</span>, <span className="font-mono">[text](https://…)</span></p>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60 mt-5">Schedule For Later <span className="text-charcoal/35 normal-case tracking-normal">(optional)</span></label>
            <input type="datetime-local" value={bulkForm.scheduled_at} onChange={(e) => setBulkForm({ ...bulkForm, scheduled_at: e.target.value })} data-testid="bulk-email-schedule"
              className="w-full border-b border-charcoal/30 bg-transparent py-2 font-sans text-sm focus:outline-none focus:border-olive" />
            {bulkForm.scheduled_at && (
              <>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60 mt-5">Repeat</label>
                <select value={bulkForm.recurrence} onChange={(e) => setBulkForm({ ...bulkForm, recurrence: e.target.value })} data-testid="bulk-email-recurrence"
                  className="w-full border border-charcoal/20 bg-transparent px-3 py-2.5 mt-1 font-mono text-xs uppercase tracking-widest focus:outline-none focus:border-olive">
                  <option value="none">Once (no repeat)</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </>
            )}
            <div className="flex flex-wrap gap-3 mt-8">
              {bulkForm.scheduled_at ? (
                <button type="button" onClick={scheduleSend} disabled={busy} data-testid="bulk-email-schedule-btn" className="flex-1 min-w-[120px] bg-navy text-bone py-3 font-mono text-xs uppercase tracking-widest hover:bg-charcoal transition-colors disabled:opacity-50">
                  {busy ? "Scheduling..." : "Schedule"}
                </button>
              ) : (
                <button type="submit" disabled={busy} data-testid="bulk-email-send" className="flex-1 min-w-[120px] bg-charcoal text-bone py-3 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors disabled:opacity-50">
                  {busy ? "Sending..." : "Send Now"}
                </button>
              )}
              <button type="button" onClick={saveDraft} disabled={busy} data-testid="bulk-email-draft-btn" className="flex-1 min-w-[120px] border border-charcoal/30 py-3 font-mono text-xs uppercase tracking-widest hover:bg-sand transition-colors disabled:opacity-50">
                Save Draft
              </button>
            </div>
          </form>
        </div>
      )}

      {/* EMAIL MEMBER MODAL */}
      {emailUser && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-testid="email-user-modal">
          <div className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm" onClick={() => !busy && setEmailUser(null)} />
          <form onSubmit={sendMemberEmail} className="relative bg-bone w-full max-w-md p-8 border border-charcoal/20">
            <button type="button" onClick={() => setEmailUser(null)} data-testid="email-user-close" className="absolute top-4 right-4 text-charcoal/40 hover:text-rust"><X size={18} /></button>
            <h3 className="font-serif text-3xl tracking-tight">Email {emailUser.name}</h3>
            <p className="font-sans text-sm text-charcoal/55 mt-1 font-mono">{emailUser.email}</p>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60 mt-6">Subject</label>
            <input value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} required data-testid="email-user-subject"
              className="w-full border-b border-charcoal/30 bg-transparent py-2 font-sans focus:outline-none focus:border-olive" />
            <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60 mt-5">Message</label>
            <textarea value={emailForm.message} onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })} required rows={5} data-testid="email-user-message"
              className="w-full border border-charcoal/20 bg-transparent p-3 mt-1 font-sans text-sm focus:outline-none focus:border-olive resize-none" />
            <button disabled={busy} data-testid="email-user-send" className="mt-8 w-full bg-olive text-bone py-3 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors disabled:opacity-50">
              {busy ? "Sending..." : "Send Email"}
            </button>
          </form>
        </div>
      )}

      {/* EDIT USER MODAL */}
      {editUser && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-testid="edit-user-modal">
          <div className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm" onClick={() => !busy && setEditUser(null)} />
          <form onSubmit={saveEdit} className="relative bg-bone w-full max-w-md p-8 border border-charcoal/20">
            <button type="button" onClick={() => setEditUser(null)} data-testid="edit-user-close" className="absolute top-4 right-4 text-charcoal/40 hover:text-rust"><X size={18} /></button>
            <h3 className="font-serif text-3xl tracking-tight">Edit User</h3>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60 mt-6">Name</label>
            <input value={editUser.name || ""} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} data-testid="edit-user-name"
              className="w-full border-b border-charcoal/30 bg-transparent py-2 font-sans focus:outline-none focus:border-olive" />
            <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60 mt-5">Email</label>
            <input type="email" value={editUser.email || ""} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} data-testid="edit-user-email"
              className="w-full border-b border-charcoal/30 bg-transparent py-2 font-sans focus:outline-none focus:border-olive" />
            <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60 mt-5">Role</label>
            <select value={editUser.role} onChange={(e) => setEditUser({ ...editUser, role: e.target.value })} data-testid="edit-user-role"
              className="w-full border border-charcoal/20 bg-transparent px-3 py-2.5 mt-1 font-mono text-xs uppercase tracking-widest focus:outline-none focus:border-olive">
              <option value="member">Member</option>
              <option value="supporter">Supporter</option>
              <option value="admin">Admin</option>
            </select>
            <label className="flex items-center gap-3 mt-5 font-sans text-sm cursor-pointer">
              <input type="checkbox" checked={!!editUser.is_supporter} onChange={(e) => setEditUser({ ...editUser, is_supporter: e.target.checked })} data-testid="edit-user-supporter" className="w-4 h-4 accent-navy" />
              Supporter status
            </label>
            <button disabled={busy} data-testid="edit-user-save" className="mt-8 w-full bg-charcoal text-bone py-3 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors disabled:opacity-50">
              {busy ? "Saving..." : "Save Changes"}
            </button>
          </form>
        </div>
      )}

      {/* DELETE CONFIRM MODAL */}
      {deleteUser && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4" data-testid="delete-user-modal">
          <div className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm" onClick={() => !busy && setDeleteUser(null)} />
          <div className="relative bg-bone w-full max-w-sm p-8 border border-charcoal/20">
            <Trash2 size={28} className="text-rust" strokeWidth={1.5} />
            <h3 className="font-serif text-2xl tracking-tight mt-4">Remove {deleteUser.name}?</h3>
            <p className="font-sans text-sm text-charcoal/60 mt-2">This permanently deletes <span className="font-mono">{deleteUser.email}</span> and cannot be undone.</p>
            <div className="flex gap-3 mt-8">
              <button onClick={() => setDeleteUser(null)} disabled={busy} className="flex-1 border border-charcoal/20 py-3 font-mono text-xs uppercase tracking-widest hover:bg-sand transition-colors">Cancel</button>
              <button onClick={confirmDelete} disabled={busy} data-testid="confirm-delete-user" className="flex-1 bg-rust text-bone py-3 font-mono text-xs uppercase tracking-widest hover:bg-rust/80 transition-colors disabled:opacity-50">
                {busy ? "Removing..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-bone p-6" data-testid={testid}>
      <Icon size={20} strokeWidth={1.4} className="text-olive" />
      <div className="font-serif text-4xl tracking-tight mt-4" data-testid={`${testid}-value`}>
        {value === undefined || value === null ? "—" : value}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-charcoal/50 mt-2">{label}</div>
      {sub && <div className="font-sans text-xs text-charcoal/45 mt-1">{sub}</div>}
    </div>
  );
}
